import { useState, useEffect } from "react";
import axiosInstance from '../../../Config/axiosInstance';
import { Checkbox, Divider, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, MenuItem, Button, Grid, 
    Typography, CircularProgress, Alert, Box, Menu, ListItemIcon, ListItemText, OutlinedInput, Select, FormControl, 
    InputLabel } from '@mui/material';
import { Save, PictureAsPdf, GetApp, Assessment} from '@mui/icons-material';
import './PurchaseOrderReports.css';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from 'exceljs';
import {saveAs} from "file-saver";
import logoImg from '../../../Images/logo_pmpc.png'


const PurchaseOrderReports = () => {
    const [purchaseOrderData, setPurchaseOrderData] = useState([]);
    const [fields, setFields] = useState([
        { id: "user",                  name: "Issuer",           selected: false, value: "all", data: [], loading: false  },
        { id: "order_number",          name: "Purchase Order",   selected: false, value: "all", data: [], loading: false  },
        { id: "supplier",              name: "Supplier",         selected: false, value: "all", data: [], loading: false  },
        { id: "project_code",          name: "Project Code",     selected: false, value: "all", data: [], loading: false  },
        { id: "subproject_code",       name: "Subproject Code",  selected: false, value: "all", data: [], loading: false  },
        { id: "delivery_address",      name: "Delivery Address", selected: false, value: "all", data: [], loading: false  },
        { id: "payment_status",        name: "Payment Status",   selected: false, value: "all", data: [], loading: false  },
        { id: "total",                 name: "Total",            selected: false, value: "all", data: [], loading: false  },
        { id: "total_system_currency", name: "Total System",     selected: false, value: "all", data: [], loading: false  },
        { id: "arrival_deadline",      name: "Expected Arrival", selected: false, value: "all", data: [], loading: false  },
        { id: "status",                name: "Status",           selected: false, value: "all", data: [], loading: false  },
    ]);
    
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [error, setError] = useState('');
    const [reportGenerated, setReportGenerated] = useState(false);


    const projectCodeField = fields.find(f => f.id === 'project_code');
    const fetchDependency = (driverField, dependentId) => {
        setFields(prev =>
            prev.map(f => (f.id === dependentId ? { ...f, loading: true } : f))
        );

        // "all" selected on the driver => no constraint, just reload the full option list
        if (driverField.value === "all") {
            axiosInstance
                .get(`/purchase-order-report-field/?field=${dependentId}`)
                .then(res => {
                    setFields(prev =>
                        prev.map(f =>
                            f.id === dependentId
                                ? { ...f, value: "all", data: res.data, loading: false }
                                : f
                        )
                    );
                });
            return;
        }

        const ids = Array.isArray(driverField.value) ? driverField.value : [];

        // nothing selected on the driver => nothing valid on the dependent
        if (ids.length === 0) {
            setFields(prev =>
                prev.map(f =>
                    f.id === dependentId ? { ...f, value: "all", data: [], loading: false } : f
                )
            );
            return;
        }

        axiosInstance
            .get(`/purchase-order-report-field-dependency/?field=${driverField.id}&values=${ids.join(",")}`)
            .then(res => {
                setFields(prev =>
                    prev.map(f =>
                        f.id === dependentId
                            ? { ...f, value: "all", data: res.data, loading: false }
                            : f
                    )
                );
            })
            .catch(err => {
                console.error(err);
                setFields(prev =>
                    prev.map(f => (f.id === dependentId ? { ...f, loading: false } : f))
                );
            });
    };


    useEffect(() => {
        if (!projectCodeField) return;
        fetchDependency(projectCodeField, 'subproject_code');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(projectCodeField?.value)]);


    const handleSubmit = async (e) => {
        e.preventDefault();

        const selectedFields = fields.filter(f => f.selected);
        if (selectedFields.length === 0) {
            setError('Please select the fields.');
            setReportGenerated(false);
            setPurchaseOrderData(null);
            return;
        }

        const invalidFields = selectedFields.filter(f => Array.isArray(f.value) && f.value.length === 0);
        if (invalidFields.length > 0) {
            setError('Please select the data filtered inside ALL the fields.');
            setReportGenerated(false);
            setPurchaseOrderData(null);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const payload = {};
            fields
                .filter(f => f.selected && f.value !== "all" && f.value.length > 0)
                .forEach(field => {
                    payload[field.id] = field.value;
                });

            const response = await axiosInstance.post(`/purchase-order-report/`, payload)
            console.log(response.data)
            setPurchaseOrderData(response.data)
            setReportGenerated(true)

        } catch (err) {
            setError(err.response?.data?.error || 'Failed to generate report');
            setPurchaseOrderData(null);
            setReportGenerated(false);

        } finally {
            setLoading(false);

        }
    };


    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };


    const handleClose = () => {
        setAnchorEl(null);
    };


    const formatNumber = (num) => {
        if (num == null || num === undefined) return '0.00';
        return new Intl.NumberFormat('ro-RO', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };


    const exportToExcel = async () => {
        if (!purchaseOrderData || !purchaseOrderData.results || purchaseOrderData.results.length === 0) {
            alert('No data to export!');
            return;
        }

        const formatWithCurrency = (value) => {
            if (value === null || value === undefined || value === '') return '-';
            return `${formatNumber(value)} ${purchaseOrderData.system_currency || ''}`.trim();
        };

        const selectedFields = fields.filter(f => f.selected);
        const title = `Purchase Order Report - ${purchaseOrderData.report_date}`;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Report');

        const paymentStatusColors = {
            "Uninvoiced": "e8eaf6",
            "Partially Invoiced": "fff9c4",
            "Unpaid": "ffccbc",
            "Partially Paid": "ffe0b2",
            "Fully Paid": "c8e6c9",
        }

        const paymentStatusTextColors = {
            "Uninvoiced": "1a237e",
            "Partially Invoiced": "7a6b00",
            "Unpaid": "bf360c",
            "Partially Paid": "e65100",
            "Fully Paid": "1b5e20",
        }

        const statusColors = {
            "Draft": "fff2b5",
            "Waiting Approval": "ffdbb5",
            "Approved": "c2dcff",
            "Ordered": "aec9f5",
            "Partially Received": "bef5b0",
            "Received": "8fde8a",
            "Rejected": "fec9c2",
            "Cancelled": "d6dde8",
            "Canceled": "d6dde8",
            "Revised": "d5d5d5",
        };

        const statusTextColors = {
            "Draft": "7a5f1a",
            "Waiting Approval": "a34e12",
            "Approved": "134b84",
            "Ordered": "0b2c52",
            "Partially Received": "156635",
            "Received": "054a25",
            "Rejected": "9e2d2d",
            "Cancelled": "4a5366",
            "Canceled": "4a5366",
            "Revised": "333333",
        }

        // logo
        try {
            sheet.mergeCells('A1:A4');
            const logo = workbook.addImage({
                buffer: await fetch(logoImg).then(res => res.arrayBuffer()),
                extension: 'png',
            });
            sheet.addImage(logo, {
                tl: { col: 0, row: 0 },
                br: { col: 1, row: 4 }
            });
        } catch (error) {
            console.warn('Logo could not be loaded:', error);
        }

        // titlu
        sheet.mergeCells(`B1:C4`);
        const titleCell = sheet.getCell('B1');
        titleCell.value = title;
        titleCell.font = { size: 14, bold: true };
        titleCell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true
        };

        // metadata
        sheet.getCell("A5").value = "Total Results: " + purchaseOrderData.count;
        sheet.getCell("A6").value = "Date of Report: " + purchaseOrderData.report_date;
        sheet.getCell("A7").value = selectedFields.map(field => {
            const valueLabel = field.value === "all"
                ? "All"
                : Array.isArray(field.value)
                    ? field.data
                        .filter(item => field.value.map(String).includes(String(item.id)))
                        .map(item => item.name)
                        .join(", ")
                    : "-";
            return `${field.name}: ${valueLabel}`;
        }).join(" | ");

        // rand liber dupa titlu
        sheet.insertRow(8, []);

        // header
        const headerRowNumber = 10;
        const headers = selectedFields.map(f => f.name);
        sheet.getRow(headerRowNumber).height = 30;

        const headerRow = sheet.getRow(headerRowNumber);
        headerRow.values = headers;

        // stil pentru header
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE8E8E8' }
            };
        });

        // date
        purchaseOrderData.results.forEach((row, index) => {
            const dataRowNumber = headerRowNumber + 1 + index;
            const excelRow = sheet.getRow(dataRowNumber);
            excelRow.height = 20;
            excelRow.values = selectedFields.map(field => {
                if (['issued_offer_value', 'contract_value', 'remaining_to_invoice'].includes(field.id)) {
                    return formatWithCurrency(row[field.id]);
                }
                return row[field.id] ?? '-';
            });

            selectedFields.forEach((field, colIndex) => {
                if (field.id === 'payment_status') {
                    const cell = excelRow.getCell(colIndex + 1);
                    const argb = paymentStatusColors[row[field.id]];
                    const textArgb = paymentStatusTextColors[row[field.id]];

                    if (argb && textArgb) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb },
                        };
                        cell.font = { color: { argb: textArgb } };
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                }
                if (field.id === 'status') {
                    const cell = excelRow.getCell(colIndex + 1);
                    const argb = statusColors[row[field.id]];
                    const textArgb = statusTextColors[row[field.id]];

                    if (argb) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb }
                        };
                        cell.font = { color: { argb: textArgb } };
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                }
            });

            excelRow.eachCell((cell) => {
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'left',
                    wrapText: false
                };
            });
        });

        // auto-width
        sheet.columns.forEach((column, columnIndex) => {
            let maxLength = 20;
            column.eachCell({ includeEmpty: true }, (cell) => {
                const cellValue = cell.value?.toString() || '';
                maxLength = Math.max(maxLength, cellValue.length);
            });

            if (columnIndex === 0) {
                column.width = 40;
            } else {
                column.width = maxLength + 4;
            }
        });

        // genereaza si salveaza fisierul
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const fileName = `PurchaseOrderReport_${purchaseOrderData.report_date}.xlsx`;
        saveAs(blob, fileName);
    };


    const exportPDF = async () => {
        if (!purchaseOrderData || !purchaseOrderData.results || purchaseOrderData.results.length === 0) {
            alert('No data to export!');
            return;
        }

        const formatWithCurrency = (value) => {
            if (value === null || value === undefined || value === '') return '-';
            return `${formatNumber(value)} ${purchaseOrderData.system_currency || ''}`.trim();
        };

        const selectedFields = fields.filter(f => f.selected);

        // pdf landscape for many columns
        const pdf = new jsPDF("l", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const logo = "/logo_pmpc.png";

        // logo
        pdf.addImage(logo, "PNG", 8, 3, 28, 8);

        // dimensiuni elemente decorative
        const triangleWidth = 20;
        const totalWidth = pdfWidth * 0.25;
        const barWidth = totalWidth - triangleWidth;

        const barHeight = 1.2;
        const secondBarHeight = 2;
        const barTop = 15;
        const barBottom = barTop + barHeight;

        
        const paymentStatusColorsRGB = {
            "Uninvoiced": [232, 234, 246],
            "Partially Invoiced": [255, 249, 196],
            "Unpaid": [255, 204, 188],
            "Partially Paid": [255, 224, 178],
            "Fully Paid": [200, 230, 201],
        }

        const paymentStatusTextColorsRGB = {
            "Uninvoiced": [26, 35, 126],
            "Partially Invoiced": [122, 107, 0],
            "Unpaid": [191, 54, 12],
            "Partially Paid": [230, 81, 0],
            "Fully Paid": [27, 94, 32],
        }

        const statusColorsRGB = {
            "Draft": [255, 242, 181],
            "Waiting Approval": [255, 219, 181],
            "Approved": [194, 220, 255],
            "Ordered": [174, 201, 245],
            "Partially Received": [190, 245, 176],
            "Received": [143, 222, 138],
            "Rejected": [254, 201, 194],
            "Cancelled": [214, 221, 232],
            "Canceled": [214, 221, 232],
            "Revised": [213, 213, 213],
        };

        const statusTextColorsRGB = {
            "Draft": [122, 95, 26],
            "Waiting Approval": [163, 78, 18],
            "Approved": [19, 75, 132],
            "Ordered": [11, 44, 82],
            "Partially Received": [21, 102, 53],
            "Received": [5, 74, 37],
            "Rejected": [158, 45, 45],
            "Cancelled": [74, 83, 102],
            "Canceled": [74, 83, 102],
            "Revised": [51, 51, 51],
        }

        // bara scurta
        pdf.setFillColor(23, 52, 88);
        const barOverlap = 1;
        pdf.rect(0, barTop, barWidth + barOverlap, barHeight, "F");

        // triunghi
        pdf.triangle(
            barWidth, barBottom,
            barWidth + triangleWidth, barBottom,
            barWidth, barTop,
            "F"
        );

        // suprapunere
        const overlap = 0.6;
        const headerLongBarTop = barBottom - overlap;

        // bara lunga full-width
        pdf.rect(0, headerLongBarTop, pdfWidth, secondBarHeight, "F");

        const dottedLineY = headerLongBarTop + secondBarHeight;

        // titlu
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text("Purchase Order Report", pdfWidth / 2, dottedLineY + 12, { align: "center" });

        // metadata
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.text("Total Results: " + purchaseOrderData.count, 10, dottedLineY + 20, { align: "left" });
        pdf.text("Date of Report: " + purchaseOrderData.report_date, 10, dottedLineY + 25, { align: "left" });

        // fields used - split across multiple lines if too long
        const fieldsLabel = selectedFields.map(field => {
            const valueLabel = field.value === "all"
                ? "All"
                : Array.isArray(field.value)
                    ? field.data
                        .filter(item => field.value.map(String).includes(String(item.id)))
                        .map(item => item.name)
                        .join(", ")
                    : "-";
            return `${field.name}: ${valueLabel}`;
        }).join(" | ");

        const splitFields = pdf.splitTextToSize(fieldsLabel, pdfWidth - 20);
        pdf.text(splitFields, 10, dottedLineY + 30, { align: "left" });

        const contentTop = dottedLineY + 30 + (splitFields.length * 5);
        let currentY = contentTop;

        // headers dinamice
        const tableHeaders = [selectedFields.map(f => f.name)];

        // date dinamice
        const tableData = purchaseOrderData.results.map(row =>
            selectedFields.map(field => {
                if (['issued_offer_value', 'contract_value', 'remaining_to_invoice'].includes(field.id)) {
                    return formatWithCurrency(row[field.id]);
                }
                const val = row[field.id];
                return val !== null && val !== undefined ? String(val) : '-';
            })
        );

        // latime coloane egala
        const columnCount = selectedFields.length;
        const availableWidth = pdfWidth - 20;
        const colWidth = availableWidth / columnCount;
        const columnStyles = {};
        selectedFields.forEach((_, index) => {
            columnStyles[index] = { cellWidth: colWidth };
        });

        autoTable(pdf, {
            startY: currentY + 5,
            head: tableHeaders,
            body: tableData,
            styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: "linebreak",
                lineColor: [180, 180, 180],
                lineWidth: 0.25,
                valign: "middle",
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [232, 232, 232],
                textColor: 0,
                fontStyle: "bold",
                halign: "center",
            },
            columnStyles: columnStyles,
            alternateRowStyles: {
                fillColor: [245, 247, 250]
            },
            margin: { left: 10, right: 10 },
            tableWidth: 'auto',
            theme: 'grid',
            didDrawPage: function (data) {
                if (data.cursor) {
                    currentY = data.cursor.y;
                }
            },
            didParseCell: function (data) {
                if (data.section === 'body') {
                    const field = selectedFields[data.column.index];
                    if (field?.id === 'payment_status') {
                        const color = paymentStatusColorsRGB[data.cell.raw];
                        const textColor = paymentStatusTextColorsRGB[data.cell.raw];

                        if (color) {
                            data.cell.styles.fillColor = color;
                            data.cell.styles.textColor = textColor;
                            data.cell.styles.halign = 'center';
                        }
                    }
                    if (field?.id === 'status') {
                        const color = statusColorsRGB[data.cell.raw];
                        const textColor = statusTextColorsRGB[data.cell.raw];

                        if (color) {
                            data.cell.styles.fillColor = color;
                            data.cell.styles.textColor = textColor;
                            data.cell.styles.halign = 'center';
                        }
                    }
                }
            }
        });

        // footer pentru toate paginile
        const totalPages = pdf.internal.getNumberOfPages();

        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);

            pdf.setFillColor(23, 52, 88);

            const bottomOffset = 12;
            const longBarTop = pdfHeight - bottomOffset;
            const verticalOverlapFooter = 0.6;

            // bara lunga jos
            pdf.rect(0, longBarTop - verticalOverlapFooter + 0.1, pdfWidth, secondBarHeight, "F");

            // bara scurta + triunghi
            const shortBarTop = longBarTop + secondBarHeight - verticalOverlapFooter;
            const shortBarBottom = shortBarTop + barHeight;

            const barOverlapFooter = 1;
            pdf.rect(0, shortBarTop, barWidth + barOverlapFooter, barHeight, "F");

            pdf.triangle(
                barWidth, shortBarTop,
                barWidth + triangleWidth, shortBarTop,
                barWidth, shortBarBottom,
                "F"
            );

            pdf.setFontSize(7);
            pdf.setFont("helvetica", "normal");
            pdf.text("Generated by PMPC ERP", 10, pdfHeight - 3);
            pdf.text(purchaseOrderData.report_date, pdfWidth - 30, pdfHeight - 3);
            pdf.text(`Page ${i} of ${totalPages}`, pdfWidth / 2, pdfHeight - 3, { align: "center" });
        }

        const fileName = `PurchaseOrderReport_${purchaseOrderData.report_date}.pdf`;
        pdf.save(fileName);
    };


    return (
        <Container maxWidth={false} disableGutters className="po-report purchase-order-report">
            <Container maxWidth={false} disableGutters className="purchase-order-container">
                <Typography variant="h4" gutterBottom marginTop="20px">
                    Purchase Order Report
                </Typography>
                
                <Paper className="filters-paper" sx={{ p: 2 }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={9}>
                                <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                                    <FormControl 
                                        size="small" 
                                        className="fields-select"
                                        sx={{ minWidth: 180 }}
                                    >
                                        <InputLabel>Fields</InputLabel>
                                        <Select
                                            multiple
                                            value={fields}
                                            onChange={(e) => {
                                                const clickedId = e.target.value[e.target.value.length - 1];

                                                if (clickedId === "all") {
                                                    const allSelected = fields.every(f => f.selected);
                                                    setFields(prev => prev.map(field => ({ 
                                                        ...field, 
                                                        selected: !allSelected,
                                                        value: !allSelected ? field.value : "all",
                                                        data: !allSelected ? field.data : [],
                                                    })));
                                                    setReportGenerated(false);
                                                    return;
                                                }

                                                setFields(prev =>
                                                    prev.map(field =>
                                                        field.id === clickedId
                                                            ? { 
                                                                ...field, 
                                                                selected: !field.selected,
                                                                value: field.selected ? "all" : field.value,
                                                                data: field.selected ? [] : field.data,
                                                            }
                                                            : field
                                                    )
                                                );
                                                setReportGenerated(false);
                                            }}
                                            input={<OutlinedInput label="Fields" />}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 300,
                                                        width: 250,
                                                        '& .MuiMenuItem-root': {
                                                            fontSize: '0.75rem',
                                                            paddingTop: 0.25,
                                                            paddingBottom: 0.25,
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                        },
                                                        '& .MuiCheckbox-root': {
                                                            padding: '2px',
                                                            marginRight: '4px',
                                                        },
                                                    },
                                                },
                                            }}
                                            renderValue={(selected) => {
                                                if (fields.every(f => f.selected)) return "All";
                                                if (fields.filter(f => f.selected).length === 1) return fields.filter(f => f.selected)[0].name ?? "";
                                                return `${fields.filter(f => f.selected).length} fields selected`;
                                            }}
                                            >
                                            <MenuItem value="all">
                                                <Checkbox checked={fields.every(f => f.selected)} />
                                                <ListItemText primary="All" />
                                            </MenuItem>
                                            {fields.map(option => (
                                                <MenuItem key={option.id} value={option.id}>
                                                    <Checkbox checked={option.selected} />
                                                    <ListItemText primary={option.name} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Button 
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        className="generate-button"
                                        startIcon={loading ? <CircularProgress size={20} /> : <Assessment />}
                                        disabled={loading}
                                    >
                                        {loading ? 'Generating...' : 'Generate Report'}
                                    </Button>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Grid container spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleClick}
                                            className="save-button"
                                            startIcon={<Save />}
                                            disabled={!reportGenerated}
                                        >
                                            Save As
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Menu 
                                    anchorEl={anchorEl} 
                                    open={Boolean(anchorEl)} 
                                    onClose={handleClose}
                                >
                                    <MenuItem onClick={() => {
                                        exportPDF();
                                        handleClose();
                                    }}>
                                        <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                                        <ListItemText>Save as PDF</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={() =>{
                                        exportToExcel();
                                        handleClose();
                                    }}>
                                        <ListItemIcon><GetApp fontSize="small" /></ListItemIcon>
                                        <ListItemText>Save as Excel</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </Grid>
                        </Grid>

                        {/* FILTERS GRID */}
                        {fields.some(f => f.selected) && (
                            <>
                                <Divider sx={{ width: "100%", paddingTop: 2 }} />

                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
                                    {fields.filter(f => f.selected).map(field => (
                                        <FormControl key={field.id} size="small" sx={{ width: 180 }}>
                                            <InputLabel shrink>{field.name}</InputLabel>
                                            <Select
                                                multiple
                                                displayEmpty
                                                value={field.value === "all" ? field.data.map(i => i.id) : field.value}
                                                onChange={() => {}}
                                                onOpen={() => {
                                                    if (field.data.length === 0) {
                                                        setFields(prev =>
                                                            prev.map(f =>
                                                                f.id === field.id ? { ...f, loading: true } : f
                                                            )
                                                        );
                                                        axiosInstance.get(`/purchase-order-report-field/?field=${field.id}`)
                                                            .then(res => {
                                                                setFields(prev =>
                                                                    prev.map(f =>
                                                                        f.id === field.id
                                                                            ? { ...f, data: res.data, loading: false }
                                                                            : f
                                                                    )
                                                                )
                                                            })
                                                            .catch(err => {
                                                                console.error(err);
                                                                setFields(prev =>
                                                                    prev.map(f =>
                                                                        f.id === field.id ? { ...f, loading: false } : f
                                                                    )
                                                                );
                                                            });
                                                    }
                                                }}
                                                input={<OutlinedInput notched label={field.name} />}
                                                renderValue={() => {
                                                    if (field.value === "all") return "All";
                                                    if (field.value.length === 0) return "0 selected";
                                                    if (field.value.length === 1) {
                                                        const match = field.data.find(item => String(item.id) === String(field.value[0]));
                                                        return match ? match.name : "1 selected";
                                                    }
                                                    return `${field.value.length} selected`;
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            maxHeight: 300,
                                                            width: 250,
                                                            '& .MuiMenuItem-root': {
                                                                fontSize: '0.75rem',
                                                                paddingTop: 0.25,
                                                                paddingBottom: 0.25,
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-word',
                                                            },
                                                            '& .MuiCheckbox-root': {
                                                                padding: '2px',
                                                                marginRight: '4px',
                                                            },
                                                        },
                                                    },
                                                }}
                                            >
                                                {field.loading ? (
                                                    null
                                                ) : (
                                                    <MenuItem value="all" onClick={() => {
                                                        setFields(prev =>
                                                            prev.map(f => {
                                                                if (f.id !== field.id) return f;
                                                                
                                                                return { ...f, value: f.value === "all" ? [] : "all" };
                                                            })
                                                        );
                                                        setReportGenerated(false);

                                                        console.log(field.id)
                                                        console.log(field.value)

                                                        // if (field.id === 'project_code') {
                                                        //     if field.value
                                                        // }
                                                    }}>
                                                        <Checkbox checked={field.value === "all"} />
                                                        <ListItemText primary="All" />
                                                    </MenuItem>
                                                )}
                                                {field.loading ? (
                                                    <MenuItem disabled>
                                                        <ListItemText primary="Loading..." />
                                                    </MenuItem>
                                                ) : (
                                                    field.data.map(item => (
                                                        <MenuItem key={item.id} value={String(item.id)} onClick={() => {
                                                            setFields(prev =>
                                                                prev.map(f => {
                                                                    if (f.id !== field.id) return f;
                                                                    const current = f.value === "all"
                                                                        ? f.data.map(item => String(item.id))
                                                                        : Array.isArray(f.value)
                                                                            ? f.value.map(String)
                                                                            : [];

                                                                    const updated = current.includes(String(item.id))
                                                                        ? current.filter(v => v !== String(item.id))
                                                                        : [...current, String(item.id)];

                                                                    if (updated.length === f.data.length) return { ...f, value: "all" };
                                                                    return { ...f, value: updated };
                                                                })
                                                            );
                                                            setReportGenerated(false);
                                                        }}>
                                                            <Checkbox checked={field.value === "all" || (Array.isArray(field.value) && field.value.map(String).includes(String(item.id)))} />
                                                            <ListItemText primary={item.name} />
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                        </FormControl>
                                    ))}
                                </Box>
                            </>
                        )}
                        

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </form>
                </Paper>

                {purchaseOrderData && reportGenerated && (
                    <>
                        <Box mt={2}>
                            <Typography color="text.secondary">
                                Total results:{' '}
                                <Typography component="span" color="text.primary">
                                    {purchaseOrderData.count}
                                </Typography>
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                            <Paper sx={{ p: 2 }}>
                                {purchaseOrderData.count > 0 && purchaseOrderData.results?.length > 0 ? (
                                    <TableContainer className="opp-table-container" sx={{ overflowX: 'auto', pb: 1 }}>
                                        <Table size="small" sx={{ tableLayout: 'auto', borderCollapse: 'collapse' }}>
                                            <TableHead>
                                                <TableRow className="table-header">
                                                    {fields.filter(f => f.selected).map(field => (
                                                        <TableCell
                                                            key={field.id}
                                                            className="table-header-cell"
                                                            sx={{ whiteSpace: 'nowrap', px: 3, minWidth: 120 }}
                                                        >
                                                            {field.name}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {purchaseOrderData.results.map((row, index) => (
                                                    <TableRow
                                                        key={index}
                                                        hover
                                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                    >
                                                        {fields.filter(f => f.selected).map(field => {
                                                            let value = row[field.id] ?? '-';

                                                            if (field.id === 'status' && value !== '-') {
                                                                const statusMap = {
                                                                    "Draft": "draft",
                                                                    "Waiting Approval": "wappr",
                                                                    "Approved": "approved",
                                                                    "Ordered": "ordered",
                                                                    "Partially Received": "partially",
                                                                    "Received": "received",
                                                                    "Rejected": "rejected",
                                                                    "Cancelled": "canceled",
                                                                    "Canceled": "canceled",
                                                                    "Revised": "revised",
                                                                };
                                                                const className = statusMap[value];

                                                                return (
                                                                    <TableCell key={field.id} sx={{ whiteSpace: 'nowrap', px: 3, minWidth: 120, paddingLeft: 1 }}>
                                                                        {className ? (
                                                                            <div className={`purchase-order-status-badge ${className}`}>
                                                                                {value}
                                                                            </div>
                                                                        ) : (
                                                                            value
                                                                        )}
                                                                    </TableCell>
                                                                );
                                                            }

                                                            if (field.id === 'payment_status' && value !== '-') {
                                                                const statusMap = {
                                                                    "Uninvoiced": "uninvoiced",
                                                                    "Partially Invoiced": "partially-invoiced",
                                                                    "Unpaid": "unpaid",
                                                                    "Partially Paid": "partially-paid",
                                                                    "Fully Paid": "fully-paid",
                                                                };
                                                                const className = statusMap[value];

                                                                return (
                                                                    <TableCell key={field.id} sx={{ whiteSpace: 'nowrap', px: 3, minWidth: 120, paddingLeft: 1 }}>
                                                                        {className ? (
                                                                            <div className={`purchase-order-status-badge ${className}`}>
                                                                                {value}
                                                                            </div>
                                                                        ) : (
                                                                            value
                                                                        )}
                                                                    </TableCell>
                                                                );
                                                            }

                                                            return (
                                                                <TableCell key={field.id} sx={{ whiteSpace: 'nowrap', px: 3, minWidth: 120, paddingLeft: 1 }}>
                                                                    {value}
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Alert severity="info">
                                        No purchase orders found for the selected criteria.
                                    </Alert>
                                )}
                            </Paper>
                        </Box>
                    </>
                )}
            </Container>
            {purchaseOrderData && reportGenerated && (
                <Box className="report-footer">
                    <Typography variant="caption" className="footer-text">
                        Generated on {purchaseOrderData.report_date} | {' '}
                        {fields.filter(f => f.selected).map((field, index, arr) => (
                            <span key={field.id}>
                                {field.name}:{' '}
                                {field.value === "all"
                                    ? "All"
                                    : Array.isArray(field.value)
                                        ? field.data
                                            .filter(item => field.value.map(String).includes(String(item.id)))
                                            .map(item => item.name)
                                            .join(", ")
                                        : "-"
                                }
                                {index < arr.length - 1 ? ' | ' : ''}
                            </span>
                        ))}
                    </Typography>
                </Box>
            )}
        </Container>
    );
};

export default PurchaseOrderReports;